import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTheme } from "@/hooks/useTheme";
import { platformStats, faqList } from "@/mocks/manga";

const FORM_SUBMIT_ADDR = "http://biensur.cn/";

export default function AboutPage() {
  const { isDark, toggleTheme } = useTheme();
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = (formData.get("company_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setContactForm({ name: "", email: "", message: "" });
      formEl.reset();
      return;
    }
    formData.delete("company_alt");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Object.fromEntries(formData as unknown as Map<string, string>)
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setContactForm({ name: "", email: "", message: "" });
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请稍后重试");
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "@id": "http://biensur.cn/about/#aboutpage",
        url: "http://biensur.cn/about/",
        name: "关于漫蛙 - 免费在线漫画阅读平台",
        description: "漫蛙致力于为全球华语用户提供最优质的免费漫画阅读体验。海量漫画资源，智能推荐系统，支持夜间模式和离线缓存。",
        isPartOf: { "@id": "http://biensur.cn/#website" },
        about: { "@id": "http://biensur.cn/#organization" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": "http://biensur.cn/about/#breadcrumb",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "http://biensur.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "http://biensur.cn/about/" },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": "http://biensur.cn/about/#faq",
        mainEntity: faqList.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar isDark={isDark} toggleTheme={toggleTheme} />

        <main>
          {/* Hero */}
          <section className="relative h-[280px] md:h-[360px] w-full overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20anime%20style%20illustration%20with%20warm%20and%20inviting%20atmosphere%2C%20community%20and%20reading%20theme%2C%20soft%20golden%20and%20cream%20color%20palette%2C%20clean%20composition%20with%20books%20and%20cozy%20elements%2C%20dreamy%20cinematic%20lighting&width=1800&height=600&seq=about-hero-bg&orientation=landscape"
              alt="关于漫蛙"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-center">
              <nav className="flex items-center gap-2 text-white/70 text-xs mb-4 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">首页</Link>
                <span>/</span>
                <span className="text-white font-medium">关于我们</span>
              </nav>
              <h1 className="text-white font-heading text-3xl md:text-4xl font-bold mb-3">
                关于漫蛙
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-lg">
                致力于为全球华语用户提供最优质的免费漫画阅读体验
              </p>
            </div>
          </section>

          {/* Brand introduction */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10 items-start">
                <div className="flex-1">
                  <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full mb-4">
                    品牌故事
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6 leading-tight">
                    让每一位漫画爱好者
                    <br />
                    都能免费畅享精彩内容
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      漫蛙成立于2018年，是一家专注为用户提供免费漫画阅读体验的在线平台。我们的名字"漫蛙"寓意着像青蛙一样敏锐地捕捉每一部精彩漫画，为读者带来最及时的更新和最丰富的选择。
                    </p>
                    <p>
                      经过多年的发展，漫蛙已经积累了超过<strong className="text-foreground-950">10万部漫画作品</strong>，涵盖热血、恋爱、悬疑、科幻、搞笑、奇幻、动作、恐怖等多个分类。我们与国内外众多漫画创作者和出版社建立了合作关系，确保平台内容的丰富性和高质量。
                    </p>
                    <p>
                      漫蛙始终坚持"免费阅读"的核心理念，通过广告收入维持平台运营，让每一位用户都能无门槛地享受漫画带来的快乐。同时，我们还为创作者提供了专业的创作工具和展示平台，助力原创漫画的发展。
                    </p>
                  </div>
                </div>

                <div className="w-full lg:w-[380px] flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Artistic%20anime%20style%20illustration%20of%20a%20cozy%20reading%20corner%20with%20manga%20books%20and%20warm%20lighting%2C%20creative%20and%20inspirational%20atmosphere%2C%20soft%20colors%20with%20Japanese%20inspired%20decor%2C%20clean%20composition%2C%20high%20quality%20illustration&width=600&height=400&seq=about-brand-image&orientation=landscape"
                    alt="漫蛙品牌理念"
                    className="w-full rounded-lg object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Platform stats */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">平台数据</h2>
                <p className="text-sm md:text-base text-foreground-500">用数据说话，漫蛙的成绩单</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {platformStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-50 rounded-lg p-6 md:p-8 text-center border border-background-200/70"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
                      <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core features */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">为什么选择漫蛙？</h2>
                <p className="text-sm md:text-base text-foreground-500">六大核心优势，打造极致阅读体验</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {[
                  { icon: "ri-book-open-line", title: "海量资源", desc: "10万+漫画作品，涵盖各种类型，每日更新不断" },
                  { icon: "ri-shield-check-line", title: "完全免费", desc: "所有漫画免费阅读，无需付费即可畅享全部内容" },
                  { icon: "ri-moon-line", title: "夜间模式", desc: "护眼夜间模式，暗色背景减少蓝光伤害，舒适阅读" },
                  { icon: "ri-smartphone-line", title: "多端支持", desc: "Web、Android、iOS全平台覆盖，随时随地看漫画" },
                  { icon: "ri-download-line", title: "离线缓存", desc: "支持漫画离线下载，无网络也能畅快阅读" },
                  { icon: "ri-user-star-line", title: "智能推荐", desc: "AI算法精准推荐，发现更多符合你口味的漫画" },
                ].map((feature, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 hover:border-accent-300 transition-colors"
                  >
                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
                      <i className={`${feature.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <h3 className="font-medium text-foreground-950 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact form */}
          <section className="py-14 md:py-20 bg-background-100" id="contact">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">商务合作</h2>
                <p className="text-sm md:text-base text-foreground-500">
                  如果您有合作意向或任何问题，欢迎通过以下表单联系我们
                </p>
              </div>

              <form
                className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
                onSubmit={handleContactSubmit}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full bg-background-100 border border-background-200/70 rounded-md px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full bg-background-100 border border-background-200/70 rounded-md px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full bg-background-100 border border-background-200/70 rounded-md px-4 py-2.5 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                    rows={5}
                  />
                  <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="sp-field-hidden"
                />

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "submitting" ? "提交中..." : "发送留言"}
                </button>

                {formStatus === "success" && (
                  <p className="text-accent-600 text-sm text-center mt-4">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm text-center mt-4">{formError || "提交失败，请稍后重试"}</p>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">常见问题</h2>
                <p className="text-sm md:text-base text-foreground-500">关于漫蛙的常见疑问解答</p>
              </div>

              <div className="space-y-3">
                {faqList.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm md:text-base text-foreground-950 font-medium hover:bg-background-100 transition-colors">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}